import React, { useEffect } from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import MarketingLayout from './components/MarketingLayout'

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } },
}
const staggerContainer = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.1 } },
}

const heroFadeUp = {
  visible: { opacity: 1, y: 0, transition: { duration: 0.45, ease: 'easeOut' } },
}

const heroStaggerContainer = {
  visible: { transition: { staggerChildren: 0.08 } },
}

// ─── Service Data ─────────────────────────────────────────────────────────────
const services = [
  {
    id: 'plumbing',
    color: 'blue',
    bgGradient: 'from-blue-600 to-blue-800',
    lightBg: 'bg-blue-50',
    textColor: 'text-blue-700',
    borderColor: 'border-blue-100',
    iconBg: 'bg-blue-100',
    title: 'Plumbing',
    tagline: 'Burst Pipes, Leaks & More - Fixed Fast',
    description:
      "Plumbing emergencies can cause catastrophic water damage in minutes. SOS SMS's dispatch engine connects you with a licensed plumber in your area the moment you reach out - no waiting, no call trees.",
    icon: (
      <svg className="w-10 h-10" fill="none" stroke="currentColor" strokeWidth={1.6} viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M7 16V4m0 0H5a2 2 0 00-2 2v2a2 2 0 002 2h2m0-6h2a2 2 0 012 2v2a2 2 0 01-2 2H7M17 8V4m0 0h-2a2 2 0 00-2 2v2a2 2 0 002 2h2m0-6h2a2 2 0 012 2v2a2 2 0 01-2 2h-2m0 4v8" />
      </svg>
    ),
    issues: [
      'Burst or leaking pipes',
      'Drain clogs and blockages',
      'Water heater failures',
      'Low water pressure',
      'Running toilets',
      'Sewer line issues',
      'Fixture installations',
      'Emergency shut-off help',
    ],
    customerBenefits: [
      'Immediate response day or night',
      'Only licensed, insured plumbers',
      'Upfront $150 flat trip charge',
      'Real-time ETA via SMS',
    ],
    providerBenefits: [
      'Steady stream of verified leads',
      'Location-matched job notifications',
      'Direct Stripe Connect payouts',
      'Flexible availability settings',
    ],
    workflow: [
      { step: '1', text: 'Text "PLUMBING" or describe your issue to SOS SMS' },
      { step: '2', text: 'Provide your address or ZIP code' },
      { step: '3', text: 'Receive the top 3 nearby plumbers with ETAs' },
      { step: '4', text: 'Reply to confirm your preferred provider' },
      { step: '5', text: 'Provider arrives and resolves the issue' },
      { step: '6', text: 'Rate your experience - payment processed automatically' },
    ],
  },
  {
    id: 'electrical',
    color: 'yellow',
    bgGradient: 'from-yellow-500 to-amber-600',
    lightBg: 'bg-yellow-50',
    textColor: 'text-yellow-700',
    borderColor: 'border-yellow-100',
    iconBg: 'bg-yellow-100',
    title: 'Electrical',
    tagline: 'Power Failures & Wiring Issues - Resolved Safely',
    description:
      "Electrical problems are not just inconvenient - they can be dangerous. SOS SMS connects you with a certified, insured electrician immediately so electrical issues are resolved safely and professionally.",
    icon: (
      <svg className="w-10 h-10" fill="none" stroke="currentColor" strokeWidth={1.6} viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
      </svg>
    ),
    issues: [
      'Complete power outages',
      'Tripped circuit breakers',
      'Flickering or dimming lights',
      'Faulty outlets or switches',
      'Panel upgrades and repairs',
      'GFCI outlet failures',
      'Wiring inspections',
      'Generator hookups',
    ],
    customerBenefits: [
      'Certified electricians dispatched immediately',
      'Safety-first, insured professionals',
      'SMS-based dispatch - no account needed',
      'Transparent flat-rate trip charge',
    ],
    providerBenefits: [
      'High-urgency, high-value job leads',
      'Verified customer information upfront',
      'Stripe-powered payment processing',
      'Expand your service area easily',
    ],
    workflow: [
      { step: '1', text: 'Text SOS SMS describing your electrical issue' },
      { step: '2', text: 'Share your ZIP code or full address' },
      { step: '3', text: 'View top nearby electricians with estimated arrival times' },
      { step: '4', text: 'Select your preferred professional via SMS reply' },
      { step: '5', text: 'Electrician arrives, diagnoses, and resolves the issue' },
      { step: '6', text: 'Confirm completion and rate your service' },
    ],
  },
  {
    id: 'hvac',
    color: 'green',
    bgGradient: 'from-green-600 to-emerald-700',
    lightBg: 'bg-green-50',
    textColor: 'text-green-700',
    borderColor: 'border-green-100',
    iconBg: 'bg-green-100',
    title: 'HVAC',
    tagline: 'Heating, Cooling & Air Quality - Restored Fast',
    description:
      "A failed AC in summer or heating breakdown in winter is a serious emergency. SOS SMS dispatches certified HVAC specialists immediately - any time of day or night - so your home environment is restored quickly.",
    icon: (
      <svg className="w-10 h-10" fill="none" stroke="currentColor" strokeWidth={1.6} viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17H3a2 2 0 01-2-2V5a2 2 0 012-2h14a2 2 0 012 2v10a2 2 0 01-2 2h-2" />
      </svg>
    ),
    issues: [
      'Air conditioner not cooling',
      'Heating system failure',
      'Thermostat malfunctions',
      'Refrigerant leaks',
      'Ventilation blockages',
      'Heat pump issues',
      'Ductwork problems',
      'Preventive maintenance calls',
    ],
    customerBenefits: [
      'HVAC specialists dispatched quickly when urgent issues hit',
      'Fast dispatch during peak demand periods',
      'Vetted and licensed professionals only',
      'Clear pricing with no hidden costs',
    ],
    providerBenefits: [
      'Year-round job opportunities (all seasons)',
      'Peak demand alert notifications',
      'Location and specialization matching',
      'Instant payment processing via Stripe',
    ],
    workflow: [
      { step: '1', text: 'Text SOS SMS with your HVAC issue details' },
      { step: '2', text: 'Provide your location for proximity matching' },
      { step: '3', text: 'Receive nearby HVAC specialists with availability and ETAs' },
      { step: '4', text: 'Confirm your chosen provider by SMS' },
      { step: '5', text: 'Technician arrives, diagnoses, and repairs the system' },
      { step: '6', text: 'Rate the service - secure payment is processed' },
    ],
  },
]

// ─── Service Tab ──────────────────────────────────────────────────────────────
function ServiceSection({ service }) {
  return (
    <section id={service.id} className="py-20 scroll-mt-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="grid lg:grid-cols-2 gap-14 items-start mb-16"
        >
          <div>
            <motion.div variants={fadeUp} className={`w-20 h-20 rounded-3xl ${service.iconBg} ${service.textColor} flex items-center justify-center mb-6`}>
              {service.icon}
            </motion.div>
            <motion.p variants={fadeUp} className={`${service.textColor} font-semibold text-sm uppercase tracking-wider mb-2`}>
              SOS SMS Service
            </motion.p>
            <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-3">
              {service.title}
            </motion.h2>
            <motion.p variants={fadeUp} className="text-xl text-gray-500 font-medium mb-5">
              {service.tagline}
            </motion.p>
            <motion.p variants={fadeUp} className="text-gray-600 leading-relaxed">
              {service.description}
            </motion.p>
          </div>

          {/* Issues grid */}
          <motion.div variants={fadeUp}>
            <h3 className="font-bold text-gray-900 mb-4">Common Issues We Dispatch For:</h3>
            <div className="grid gap-3 sm:grid-cols-2">
              {service.issues.map((issue) => (
                <div key={issue} className={`flex items-center gap-2.5 px-4 py-3 rounded-xl ${service.lightBg} border ${service.borderColor}`}>
                  <svg className={`w-4 h-4 flex-shrink-0 ${service.textColor}`} fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="text-gray-700 text-sm">{issue}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </motion.div>

        {/* Workflow */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="bg-gray-50 rounded-3xl p-8 lg:p-12 mb-12"
        >
          <motion.h3 variants={fadeUp} className="text-xl font-extrabold text-gray-900 mb-8 text-center">
            How {service.title} Dispatch Works
          </motion.h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {service.workflow.map((w) => (
              <motion.div key={w.step} variants={fadeUp} className="flex items-start gap-4 bg-white rounded-2xl p-5 border border-gray-100">
                <div className={`w-9 h-9 rounded-xl bg-gradient-to-br ${service.bgGradient} text-white font-bold text-sm flex items-center justify-center flex-shrink-0`}>
                  {w.step}
                </div>
                <p className="text-gray-600 text-sm leading-relaxed">{w.text}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Benefits */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="grid md:grid-cols-2 gap-8"
        >
          <motion.div variants={fadeUp} className="bg-white rounded-2xl p-8 border border-gray-100 shadow-sm">
            <h3 className="font-extrabold text-gray-900 text-lg mb-5 flex items-center gap-2">
              <span className="text-2xl">🏠</span> For Homeowners
            </h3>
            <ul className="space-y-3">
              {service.customerBenefits.map((b) => (
                <li key={b} className="flex items-start gap-3">
                  <svg className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="text-gray-600 text-sm">{b}</span>
                </li>
              ))}
            </ul>
          </motion.div>
          <motion.div variants={fadeUp} className="bg-white rounded-2xl p-8 border border-gray-100 shadow-sm">
            <h3 className="font-extrabold text-gray-900 text-lg mb-5 flex items-center gap-2">
              <span className="text-2xl">🔧</span> For Service Providers
            </h3>
            <ul className="space-y-3">
              {service.providerBenefits.map((b) => (
                <li key={b} className="flex items-start gap-3">
                  <svg className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="text-gray-600 text-sm">{b}</span>
                </li>
              ))}
            </ul>
          </motion.div>
        </motion.div>
      </div>
    </section>
  )
}

// ─── Main Component ───────────────────────────────────────────────────────────
function ServicesPage() {
  useEffect(() => {
    document.title = 'Plumbing, Electrical & HVAC Dispatch | SOS SMS'
    document.querySelector('meta[name="description"]')?.setAttribute(
      'content',
      'Emergency plumbing, electrical, and HVAC dispatch — licensed, insured professionals connected in minutes via SMS, no app or account needed.'
    )
  }, [])

  return (
    <MarketingLayout>

      {/* ── HERO ──────────────────────────────────────────────────────────────── */}
      <section className="bg-gradient-to-br from-green-900 via-green-800 to-emerald-700 text-white py-24 lg:py-32 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="grid3" width="40" height="40" patternUnits="userSpaceOnUse">
                <path d="M 40 0 L 0 0 0 40" fill="none" stroke="white" strokeWidth="0.5" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid3)" />
          </svg>
        </div>
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial="visible" animate="visible" variants={heroStaggerContainer}>
            <motion.p variants={heroFadeUp} className="text-green-300 font-semibold text-sm uppercase tracking-wider mb-4">
              Our Services
            </motion.p>
            <motion.h1 variants={heroFadeUp} className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-tight mb-6">
              Every Home Emergency,
              <br />
              <span className="text-green-300">Covered.</span>
            </motion.h1>
            <motion.p variants={heroFadeUp} className="text-green-100 text-lg leading-relaxed max-w-2xl mx-auto mb-8">
              SOS SMS dispatches licensed professionals for plumbing, electrical, and HVAC emergencies when urgent issues hit.
            </motion.p>
            {/* Quick service nav */}
            <motion.div variants={heroFadeUp} className="flex flex-wrap justify-center gap-3">
              {services.map((s) => (
                <a
                  key={s.id}
                  href={`#${s.id}`}
                  className="px-5 py-2.5 bg-white/10 border border-white/30 text-white text-sm font-semibold rounded-full hover:bg-white/20 transition-colors backdrop-blur-sm"
                >
                  {s.title}
                </a>
              ))}
            </motion.div>
          </motion.div>
        </div>
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 60" className="w-full h-12 fill-white" preserveAspectRatio="none">
            <path d="M0,32 C360,60 1080,0 1440,32 L1440,60 L0,60 Z" />
          </svg>
        </div>
      </section>

      {/* ── PLATFORM PROMISE ──────────────────────────────────────────────────── */}
      <section className="py-14 bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 text-center">
            {[
              { icon: '🛡️', label: 'License Verified' },
              { icon: '📋', label: 'Insurance Required' },
              { icon: '⚡', label: 'Real-Time Dispatch' },
              { icon: '💳', label: '$150 Flat Trip Charge' },
            ].map((item) => (
              <div key={item.label} className="flex flex-col items-center gap-2">
                <div className="text-3xl">{item.icon}</div>
                <span className="text-gray-700 font-semibold text-sm">{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── SERVICE SECTIONS ──────────────────────────────────────────────────── */}
      {services.map((service, idx) => (
        <div key={service.id} className={idx % 2 === 1 ? 'bg-gray-50' : 'bg-white'}>
          <ServiceSection service={service} />
          {idx < services.length - 1 && (
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="border-b border-gray-100" />
            </div>
          )}
        </div>
      ))}

      {/* ── FINAL CTA ─────────────────────────────────────────────────────────── */}
      <section className="py-20 bg-gradient-to-br from-green-800 to-emerald-600 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer}>
            <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-extrabold mb-5">
              Need Help Right Now?
            </motion.h2>
            <motion.p variants={fadeUp} className="text-green-100 text-lg mb-8">
              Sign up and get access to SOS SMS's emergency dispatch network in minutes.
            </motion.p>
            <motion.div variants={fadeUp} className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/customer/signup"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-green-800 font-bold rounded-2xl hover:bg-green-50 transition-colors shadow-lg"
              >
                Get Emergency Help
                <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </Link>
              <Link
                to="/provider/signup"
                className="inline-flex items-center justify-center px-8 py-4 border-2 border-white/40 text-white font-semibold rounded-2xl hover:bg-white/10 transition-colors"
              >
                Join as Provider
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

    </MarketingLayout>
  )
}

export default ServicesPage
